/**
 * Utilities for Cloudinary image optimization on the frontend
 */

export interface CloudinaryOptions {
  width?: number;
  height?: number;
  crop?: string;
  quality?: string | number;
  format?: string;
  effect?: string;
}

/**
 * Transforms a Cloudinary URL with specified options
 */
export function transformCloudinaryUrl(url: string | undefined | null, options: CloudinaryOptions = {}): string {
  if (!url || typeof url !== 'string') return '/assets/placeholder-botanical.jpg';

  // Only process Cloudinary URLs
  if (!url.includes('res.cloudinary.com')) {
    // Fix double slash issue for local assets starting with /
    if (url.startsWith('http')) return url;
    return url.startsWith('/') ? url : '/' + url;
  }

  try {
    const parts = url.split('/');
    const uploadIndex = parts.indexOf('upload');

    if (uploadIndex === -1) return url;

    // Build transformation string
    const transformations: string[] = [];
    
    if (options.width) transformations.push(`w_${options.width}`);
    if (options.height) transformations.push(`h_${options.height}`);
    if (options.crop) transformations.push(`c_${options.crop}`);
    else if (options.width || options.height) transformations.push('c_fill'); // Default crop if dimensions provided
    
    if (options.quality) transformations.push(`q_${options.quality}`);
    else transformations.push('q_auto');
    
    if (options.format) transformations.push(`f_${options.format}`);
    else transformations.push('f_auto');

    if (options.effect) transformations.push(`e_${options.effect}`);

    const transformString = transformations.join(',');

    // Always insert new transformation segment after 'upload'
    // Cloudinary handles multiple transformation segments correctly.
    // This avoids misidentifying folder names as transformation segments.
    parts.splice(uploadIndex + 1, 0, transformString);

    return parts.join('/');
  } catch (error) {
    return url;
  }
}

/**
 * Generates a Low Quality Image Placeholder (LQIP) URL
 */
export function getLqipUrl(url: string | undefined | null): string {
  return transformCloudinaryUrl(url, {
    width: 20,
    quality: 10,
    effect: 'blur:1000'
  });
}
